import _ from 'underscore';

import {StageFlowNodeGraph, StageRunGraph} from './TaggedNodeGraph';
import {VIRTUAL_NODE_ID_PREFIX, VirtualNodeGraph} from './VirtualNodeGraph';

/**
 * Duplicate the branch of a node.
 *
 * @return {newVirtualNodeGraph, newRootNodeId}, where newRootNodeId is the ID of the node that was split
 * off from nodeId.
 */
export function duplicateBranch<T extends StageRunGraph | StageFlowNodeGraph>(
  virtualNodeGraph: VirtualNodeGraph<T>,
  nodeId: string
): {newVirtualNodeGraph: VirtualNodeGraph<T>; newRootNodeId: string} {
  const descendantNodeIds = virtualNodeGraph.getDescendantNodeIds(nodeId);

  const originalIdToClonedId = new Map();
  const clonedIdToOriginalId = new Map();
  for (const descendantNodeId of descendantNodeIds) {
    const clonedNodeId = _.uniqueId(VIRTUAL_NODE_ID_PREFIX);
    originalIdToClonedId.set(descendantNodeId, clonedNodeId);
    clonedIdToOriginalId.set(clonedNodeId, descendantNodeId);
  }

  // For each cloned node, use the cloned parent if possible, otherwise the original parent
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getClonedParentNodeIds = (clonedNodeId: string) => {
    const originalNodeId = clonedIdToOriginalId.get(clonedNodeId);
    return virtualNodeGraph.getParentNodeIds(originalNodeId).map((parentNodeId) => {
      if (originalIdToClonedId.has(parentNodeId)) {
        return originalIdToClonedId.get(parentNodeId);
      } else {
        return parentNodeId;
      }
    });
  };

  const nodesToAdd = Array.from(clonedIdToOriginalId.keys()).map((clonedNodeId) => ({
    id: clonedNodeId,
    parentIds: getClonedParentNodeIds(clonedNodeId),
    stageIndex: virtualNodeGraph.getTag(clonedIdToOriginalId.get(clonedNodeId)),
  }));

  return {
    newVirtualNodeGraph: virtualNodeGraph.addVirtualNodes(nodesToAdd),
    newRootNodeId: originalIdToClonedId.get(nodeId),
  };
}
